/**
 * @file apps/web/src/pages/DesignPage.tsx
 * @description Design phase — two tabs: Design Items (grouped by type,
 * filterable, editable via a side panel) and Documents (FDD), which renders
 * the same content as the standalone /documents route via DocumentsTabContent.
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Plus, AlertCircle } from 'lucide-react';
import { useAuth } from '../lib/auth.js';
import { designItemsApi } from '../lib/api.js';
import { notifyProjectOverviewChanged } from '../lib/projectOverviewEvents.js';
import { useProjectRefresh } from '../lib/useProjectRefresh.js';
import { Badge, type BadgeVariant } from '../components/Badge.js';
import { DesignItemPanel, type DesignItem, type DesignItemPanelTarget } from '../components/DesignItemPanel.js';
import { DocumentsTabContent } from './DocumentsPage.js';

const TYPE_LABELS: Record<string, string> = {
  suiteflow: 'SuiteFlow', suitescript: 'SuiteScript', custom_field: 'Custom Field',
  suiteapp: 'SuiteApp', bundle: 'Bundle', configuration: 'Configuration', other: 'Other',
};
const TYPE_ORDER = Object.keys(TYPE_LABELS);

// ---------------------------------------------------------------------------
// Item row / type group
// ---------------------------------------------------------------------------

function ItemRow({ item, onClick }: { item: DesignItem; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg hover:bg-gray/40 transition-colors"
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm text-ink truncate">{item.title}</p>
        {item.description && (
          <p className="text-xs text-muted truncate mt-0.5">{item.description}</p>
        )}
        {item.module_tag && (
          <p className="text-[10px] font-mono text-muted/70 mt-0.5">
            {[item.module_tag, item.process_area].filter(Boolean).join(' · ')}
          </p>
        )}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <Badge variant={item.type as BadgeVariant} size="xs" />
        <Badge variant={item.status as BadgeVariant} size="xs" />
      </div>
    </button>
  );
}

function TypeGroup({ type, items, onItemClick }: { type: string; items: DesignItem[]; onItemClick: (item: DesignItem) => void }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="card overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 p-3 hover:bg-gray/20 transition-colors"
      >
        <span className="text-sm font-medium text-ink">{TYPE_LABELS[type] ?? type}</span>
        <span className="text-[11px] font-mono text-muted">{items.length}</span>
      </button>
      {open && (
        <div className="border-t border-[rgba(8,17,31,0.06)] p-1.5 space-y-0.5">
          {items.map((item) => <ItemRow key={item.id} item={item} onClick={() => onItemClick(item)} />)}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Design Items tab
// ---------------------------------------------------------------------------

function DesignItemsTab({ projectId }: { projectId: string }) {
  const { devHeaders } = useAuth();
  const [grouped, setGrouped]         = useState<Record<string, DesignItem[]>>({});
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [panelTarget, setPanelTarget] = useState<DesignItemPanelTarget | null>(null);
  const [typeFilter, setTypeFilter]     = useState<string | null>(null);
  const [moduleFilter, setModuleFilter] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await designItemsApi.list(projectId, devHeaders) as Record<string, DesignItem[]>;
      setGrouped(data);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load design items');
    } finally {
      setLoading(false);
    }
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { void load(); }, [load]);

  const allItems = Object.values(grouped).flat();
  const moduleTags = Array.from(new Set(allItems.map((i) => i.module_tag).filter((m): m is string => !!m))).sort();

  const visibleTypes = TYPE_ORDER.filter((t) => grouped[t]?.length && (!typeFilter || typeFilter === t));
  const filteredItems = (type: string) =>
    (grouped[type] ?? []).filter((i) => !moduleFilter || i.module_tag === moduleFilter);

  const handleCreate = async (input: { title: string; description: string; type: string }) => {
    await designItemsApi.create(projectId, input, devHeaders);
    await load();
    notifyProjectOverviewChanged(projectId); // item count/status feed the Design gate
  };
  const handleUpdate = async (itemId: string, input: { title: string; description: string; type: string; status: string }) => {
    await designItemsApi.update(projectId, itemId, input, devHeaders);
    await load();
    notifyProjectOverviewChanged(projectId); // all items approved => Design completes => Build unlocks
  };

  // An FDD approval (decided in another window, e.g. the client portal) moves a
  // design item's status server-side — reload when this window is shown again.
  useProjectRefresh(projectId, () => { void load(); });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setTypeFilter(null)}
            className={['text-xs font-mono px-2.5 py-1 rounded-full border', !typeFilter ? 'border-blue text-blue bg-sky' : 'border-[rgba(8,17,31,0.12)] text-muted'].join(' ')}
          >
            All Types
          </button>
          {TYPE_ORDER.filter((t) => grouped[t]?.length).map((t) => (
            <button
              key={t}
              onClick={() => setTypeFilter((v) => (v === t ? null : t))}
              className={['text-xs font-mono px-2.5 py-1 rounded-full border', typeFilter === t ? 'border-blue text-blue bg-sky' : 'border-[rgba(8,17,31,0.12)] text-muted'].join(' ')}
            >
              {TYPE_LABELS[t] ?? t}
            </button>
          ))}
          {moduleTags.length > 0 && (
            <select
              value={moduleFilter ?? ''}
              onChange={(e) => setModuleFilter(e.target.value || null)}
              className="input text-xs py-1 w-auto"
            >
              <option value="">All Modules</option>
              {moduleTags.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          )}
        </div>
        <button onClick={() => setPanelTarget({ mode: 'create' })} className="btn-primary text-sm">
          <Plus size={14} /> New Item
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-danger/8 rounded-lg text-danger text-sm">
          <AlertCircle size={14} className="shrink-0" /> {error}
        </div>
      )}

      {loading ? (
        <div className="card h-64 animate-pulse bg-gray/60" />
      ) : allItems.length === 0 ? (
        <div className="empty-state py-16">
          <p className="text-sm text-muted">No design items yet.</p>
          <p className="text-xs text-muted/70 mt-1">
            Items are created automatically when a Discovery node is flagged for customization,
            or add one manually.
          </p>
          <button onClick={() => setPanelTarget({ mode: 'create' })} className="btn-primary mt-4">
            <Plus size={14} /> New Item
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {visibleTypes.map((type) => (
            <TypeGroup key={type} type={type} items={filteredItems(type)} onItemClick={(item) => setPanelTarget({ mode: 'edit', item })} />
          ))}
        </div>
      )}

      <DesignItemPanel
        target={panelTarget}
        projectId={projectId}
        onClose={() => setPanelTarget(null)}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// DesignPage
// ---------------------------------------------------------------------------

export function DesignPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [tab, setTab] = useState<'items' | 'documents'>('items');

  if (!projectId) return null;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-display text-xl font-semibold text-ink">Design</h2>
        <p className="text-xs font-mono text-muted mt-0.5">
          Track concrete design decisions and the Functional Design Document.
        </p>
      </div>

      <div className="flex gap-1 border-b border-[rgba(8,17,31,0.08)]">
        {(['items', 'documents'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={[
              'px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === t ? 'border-blue text-blue' : 'border-transparent text-muted hover:text-ink',
            ].join(' ')}
          >
            {t === 'items' ? 'Design Items' : 'Documents (FDD)'}
          </button>
        ))}
      </div>

      {tab === 'items' ? <DesignItemsTab projectId={projectId} /> : <DocumentsTabContent />}
    </div>
  );
}
